import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState, } from "react";
import { useBoolean } from "@fluentui/react-hooks";
import { Callout, ProgressIndicator, SearchBox, } from "@fluentui/react";
import { ElxLink } from "@elixir/components";
import { ProgressIndicatorStyle, typeAheadCalloutStyle, } from "./AutoSuggestSearchStyles";
import { useLocaleStrings } from "../../library/providers/strings";
import { visuallyHiddenStyle } from "../Common.styles";
export const AutoSuggestSearchBox = (props) => {
    var _a, _b, _c, _d;
    const { getLocalSpecificString } = useLocaleStrings();
    const [searchStatus, setSearchStatus] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const searchRequestId = useRef(0);
    const [isCalloutFocused, { setTrue: focusCallOut, setFalse: unfocusCallout },] = useBoolean(false);
    const [isCallOutVisible, { setTrue: showCallOut, setFalse: hideCallOut }] = useBoolean(false);
    const [isTabInitialized, setIsTabInitialized] = useState(false);
    //add a condition in the below useState on props.objectType for different query value based on T
    const [query, setQuery] = useState(props.objectType === "customerData"
        ? (_b = (_a = props.selectedSuggestion) === null || _a === void 0 ? void 0 : _a.companyName) !== null && _b !== void 0 ? _b : ""
        : (_d = (_c = props.selectedSuggestion) === null || _c === void 0 ? void 0 : _c.partnerName) !== null && _d !== void 0 ? _d : "");
    useEffect(() => {
        searchRequestId.current++;
        setSearchStatus("");
        setSearchResults([]);
        if (isTabInitialized) {
            setQuery("");
        }
        setIsTabInitialized(true);
    }, [props.objectType]);
    const focusZoneRef = useRef(null);
    const [suggestionClicked, { setTrue: isSuggestionClicked, setFalse: notSuggestionClicked },] = useBoolean(false);
    const textInput = useRef(null);
    const advancedSearchRef = useRef(null);
    const dropDownRefs = useRef([]);
    // Effective suggestion list actually rendered. Applies the opt-in TPID
    // de-duplication for customer suggestions: records sharing a TPID collapse to
    // a single entry whose company name best matches the typed query
    // (exact match > starts-with > shortest name), preserving the first
    // appearance order of each TPID. Records without a TPID are always kept.
    const displayedSuggestions = useMemo(() => {
        const list = searchResults;
        if (!(props.deduplicateByTpid && props.objectType === "customerData")) {
            return list;
        }
        const normalizedQuery = query.trim().toLowerCase();
        const matchRank = (name) => {
            const normalizedName = (name !== null && name !== void 0 ? name : "").trim().toLowerCase();
            if (normalizedQuery && normalizedName === normalizedQuery) {
                return 0;
            }
            if (normalizedQuery && normalizedName.startsWith(normalizedQuery)) {
                return 1;
            }
            return 2;
        };
        const isBetterRepresentative = (candidate, current) => {
            var _a, _b;
            const candidateRank = matchRank(candidate.companyName);
            const currentRank = matchRank(current.companyName);
            if (candidateRank !== currentRank) {
                return candidateRank < currentRank;
            }
            return (((_a = candidate.companyName) !== null && _a !== void 0 ? _a : "").length <
                ((_b = current.companyName) !== null && _b !== void 0 ? _b : "").length);
        };
        const orderedKeys = [];
        const bestByKey = new Map();
        list.forEach((item, index) => {
            var _a;
            const tpid = ((_a = item === null || item === void 0 ? void 0 : item.tpid) !== null && _a !== void 0 ? _a : "").trim();
            // Records without a TPID cannot be aggregated, so keep each distinct.
            const key = tpid || `__no_tpid__${index}`;
            const existing = bestByKey.get(key);
            if (!existing) {
                orderedKeys.push(key);
                bestByKey.set(key, item);
            }
            else if (isBetterRepresentative(item, existing)) {
                bestByKey.set(key, item);
            }
        });
        return orderedKeys.map((key) => bestByKey.get(key));
    }, [searchResults, props.deduplicateByTpid, props.objectType, query]);
    useEffect(() => {
        const requestId = ++searchRequestId.current;
        const timeOutId = setTimeout(() => {
            if (suggestionClicked)
                return;
            const request = props.onSearchRequested(query);
            if (!request)
                return;
            setSearchStatus("loading");
            void request.then((results) => {
                // Ignore superseded responses without changing the shared reducer's ordering.
                if (requestId === searchRequestId.current) {
                    setSearchResults(results !== null && results !== void 0 ? results : []);
                    setSearchStatus(results === undefined ? "error" : results.length === 0 ? "empty" : "results");
                }
            });
        }, props.debounceTime || 0);
        return () => {
            searchRequestId.current++;
            clearTimeout(timeOutId);
        };
    }, [query, props.objectType]);
    const onFocus = (_event) => {
        if (suggestionClicked) {
            notSuggestionClicked();
        }
        else if (query.length != 0 && !isCallOutVisible) {
            showCallOut();
        }
    };
    const onKeyDown = (ev) => {
        var _a, _b;
        switch (ev.key) {
            case "ArrowDown":
            case "Tab": {
                if (isCallOutVisible && advancedSearchRef.current) {
                    (_a = advancedSearchRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                    ev.preventDefault();
                }
                break;
            }
            case "Enter": {
                focusCallOut();
                (_b = focusZoneRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                ev.preventDefault();
                break;
            }
            default:
                unfocusCallout();
        }
    };
    const getQuery = (item) => {
        if (props.objectType) {
            if (props.objectType == "customerData") {
                return item.companyName;
            }
            else if (props.objectType == "partnerData") {
                return item.partnerName;
            }
        }
        else if (typeof item == "string") {
            return item;
        }
        return item;
    };
    const onSuggestionClicked = (suggestion) => {
        isSuggestionClicked();
        props.onSuggestionClicked(suggestion);
        setQuery(getQuery(suggestion));
        hideCallOut();
    };
    const onSuggestionKeyDown = (event, suggestion) => {
        if (event.key === "Enter")
            onSuggestionClicked(suggestion);
    };
    //method specific to customer data
    const getCustomerSuggestionItem = (suggestionItem) => {
        return (_jsxs("p", Object.assign({ className: "suggestionTitle" }, { children: [_jsxs("span", { children: [" ", suggestionItem.companyName] }), _jsx("br", {}), _jsxs("span", Object.assign({ className: "suggestionSubtitle" }, { children: ["TP ID: ", suggestionItem.tpid, !props.hideSalesId && (_jsxs(_Fragment, { children: [_jsx("span", Object.assign({ className: "seperator" }, { children: "|" })), "Sales ID: ", suggestionItem.msSalesID] }))] }))] })));
    };
    //method sepecific to partner search
    const getPartnerSuggestionItem = (suggestionItem) => {
        return (_jsxs("p", Object.assign({ className: "suggestionTitle" }, { children: [_jsxs("span", { children: [" ", suggestionItem.partnerName] }), _jsx("br", {}), _jsxs("span", Object.assign({ className: "suggestionSubtitle" }, { children: ["Partner ID: ", suggestionItem.partnerId, _jsx("span", Object.assign({ className: "seperator" }, { children: "|" })), "Parent Name: ", suggestionItem.parentName] }))] })));
    };
    const getSuggestionItem = (suggestionItem) => {
        let suggestionItemContent = _jsx(_Fragment, {});
        if (props.objectType) {
            if (props.objectType == "customerData") {
                suggestionItemContent = getCustomerSuggestionItem(suggestionItem);
            }
            else if (props.objectType == "partnerData") {
                suggestionItemContent = getPartnerSuggestionItem(suggestionItem);
            }
        }
        else {
            suggestionItemContent = _jsx(_Fragment, { children: suggestionItem });
        }
        return _jsx("div", Object.assign({ className: "suggestionItem" }, { children: suggestionItemContent }));
    };
    const renderSuggestions = () => {
        return (_jsx(_Fragment, { children: displayedSuggestions === null || displayedSuggestions === void 0 ? void 0 : displayedSuggestions.map((suggestion, i) => {
                return (_jsx(ElxLink, Object.assign({ style: { width: "100%", textDecoration: "none" }, onClick: (_ev) => onSuggestionClicked(suggestion), className: "suggestionListItem", role: "option", componentRef: (el) => (dropDownRefs.current[i] = el), onKeyDown: (ev) => {
                        var _a;
                        switch (ev.key) {
                            case "Enter":
                                onSuggestionClicked(suggestion);
                                break;
                            case "ArrowDown":
                                ev.preventDefault();
                                if (i !== displayedSuggestions.length - 1) {
                                    dropDownRefs.current[i + 1].focus();
                                }
                                break;
                            case "ArrowUp":
                                if (i !== 0) {
                                    ev.preventDefault();
                                    dropDownRefs.current[i - 1].focus();
                                }
                                else {
                                    (_a = advancedSearchRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                                }
                                break;
                            default:
                                hideCallOut();
                                break;
                        }
                    } }, { children: getSuggestionItem(suggestion) }), i));
            }) }));
    };
    const onChange = (_event, newValue) => {
        var _a, _b;
        if (suggestionClicked)
            notSuggestionClicked();
        searchRequestId.current++;
        setSearchStatus("");
        setSearchResults([]);
        setQuery(newValue || "");
        if (!isCallOutVisible && ((_a = newValue === null || newValue === void 0 ? void 0 : newValue.length) !== null && _a !== void 0 ? _a : 0) > 0)
            showCallOut();
        else if (isCallOutVisible && ((_b = newValue === null || newValue === void 0 ? void 0 : newValue.length) !== null && _b !== void 0 ? _b : 0) == 0)
            hideCallOut();
    };
    return (_jsxs("div", Object.assign({ className: "searchContainer" }, { children: [_jsx("div", Object.assign({ ref: textInput, className: props.className }, { children: _jsx(SearchBox, Object.assign({}, props, { autoComplete: "off", onChange: onChange, onFocus: onFocus, onKeyDown: onKeyDown, onKeyDownCapture: onKeyDown, value: query, "aria-label": props.ariaLabel })) })), _jsx("div", Object.assign({ role: "status", "aria-live": "polite", "aria-atomic": "true", className: visuallyHiddenStyle.srOnly }, { children: isCallOutVisible && searchStatus === "loading"
                    ? getLocalSpecificString("searchLoadingStatus")
                    : isCallOutVisible && searchStatus === "empty"
                        ? getLocalSpecificString("searchNoResultsStatus")
                        : isCallOutVisible && searchStatus === "error"
                            ? getLocalSpecificString("searchErrorStatus")
                            : "" })), isCallOutVisible && (_jsxs(Callout, Object.assign({ styles: typeAheadCalloutStyle, isBeakVisible: false, target: textInput.current, onDismiss: hideCallOut, doNotLayer: true, directionalHintFixed: true, preventDismissOnScroll: true }, { children: [searchStatus === "loading" && (_jsx(ProgressIndicator, { styles: ProgressIndicatorStyle, "aria-hidden": true })), _jsx(ElxLink, Object.assign({ name: "All", onClick: () => {
                            hideCallOut();
                            props.onShowAllClicked();
                        }, target: "_blank", className: "advancedSearchLbl", componentRef: advancedSearchRef, onKeyDown: (ev) => {
                            switch (ev.key) {
                                case "ArrowUp":
                                    if (advancedSearchRef.current) {
                                        ev.preventDefault();
                                        hideCallOut();
                                    }
                                    break;
                                case "ArrowDown":
                                    if (searchStatus === "results" &&
                                        displayedSuggestions.length > 0 &&
                                        dropDownRefs.current[0]) {
                                        ev.preventDefault();
                                        dropDownRefs.current[0].focus();
                                    }
                                    break;
                                default:
                                    hideCallOut();
                            }
                        } }, { children: "Advanced Search" })), _jsx("div", Object.assign({ id: "focusZoneSuggestions", ref: focusZoneRef, role: "group", "aria-label": "Focus Zone suggestions" }, { children: searchStatus === "results" ? (renderSuggestions()) : searchStatus === "empty" ? (_jsxs("div", Object.assign({ className: "noResultsDiv" }, { children: ["- ", getLocalSpecificString("searchNoResultsStatus"), " -", _jsx("br", {}), props.noResultMessage] }))) : searchStatus === "error" ? (_jsx("div", Object.assign({ className: "noResultsDiv" }, { children: getLocalSpecificString("searchErrorStatus") }))) : null }))] })))] })));
};
export default AutoSuggestSearchBox;
